/**
 * Wooden Town delivery estimate: real order-cutoff countdown and arrival window in business days
 * (weekends and US carrier holidays excluded).
 * Rendered by snippets/wt-delivery-estimate.liquid (product page block and cart summary).
 * Logistics values come from the "Delivery estimate" theme settings via data attributes.
 */
if (!customElements.get('wt-delivery-estimate')) {
  class WtDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      // Product page: hide the promise while a sold-out variant is selected (blocks/wt-delivery-estimate
      // renders the box hidden when the initial variant is sold out). Same event as wt-urgency.js;
      // the name is spelled out because this script has no imports.
      if (this.hasAttribute('data-track-variant')) {
        this.variantScope = this.closest('.shopify-section, dialog');
        this.variantScope?.addEventListener('shopify:product:select', this.onProductSelect);
      }
      this.update();
      this.timer = setInterval(() => this.update(), 60000);
      // The cart drawer re-renders by morphing server HTML into this element, which resets the
      // computed text to the fallback. Recompute whenever the content changes underneath us.
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.variantScope?.removeEventListener('shopify:product:select', this.onProductSelect);
    }

    /** @param {Event & { promise?: Promise<any> }} event */
    onProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      event.promise
        ?.then(({ detail } = {}) => {
          if (!detail) return;
          const available = Boolean(detail.resource?.available);
          if (this.hidden === available) this.hidden = !available;
        })
        .catch(() => {});
    };

    /** Writes only when the value differs, so the MutationObserver settles after one pass. */
    setText(selector, text) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    }

    /** Current wall-clock time in the shop's time zone, as a local Date (built from parts, no string parsing). */
    nowInZone() {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23',
        }).formatToParts(new Date());
        const get = (type) => Number(parts.find((part) => part.type === type)?.value);
        const date = new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
        return Number.isNaN(date.getTime()) ? new Date() : date;
      } catch (error) {
        return new Date();
      }
    }

    /** US carrier holidays (UPS, FedEx and USPS closed), shifted to the observed weekday. Cached per year. */
    holidaysFor(year) {
      this.holidayCache ??= new Map();
      if (!this.holidayCache.has(year)) {
        const nthWeekday = (month, weekday, n) => {
          const date = new Date(year, month, 1);
          date.setDate(1 + ((weekday - date.getDay() + 7) % 7) + (n - 1) * 7);
          return date;
        };
        const lastMonday = new Date(year, 5, 0);
        lastMonday.setDate(lastMonday.getDate() - ((lastMonday.getDay() + 6) % 7));
        const observed = (month, day) => {
          const date = new Date(year, month, day);
          if (date.getDay() === 6) date.setDate(day - 1);
          if (date.getDay() === 0) date.setDate(day + 1);
          return date;
        };
        const dates = [
          observed(0, 1), // New Year's Day
          lastMonday, // Memorial Day
          observed(6, 4), // Independence Day
          nthWeekday(8, 1, 1), // Labor Day
          nthWeekday(10, 4, 4), // Thanksgiving
          observed(11, 25), // Christmas Day
        ];
        this.holidayCache.set(year, new Set(dates.map((date) => date.toDateString())));
      }
      return this.holidayCache.get(year);
    }

    isBusinessDay(date) {
      const day = date.getDay();
      const key = date.toDateString();
      const year = date.getFullYear();
      // A New Year's Day on a Saturday is observed on Dec 31 of the previous year, so check both sets.
      return day !== 0 && day !== 6 && !this.holidaysFor(year).has(key) && !this.holidaysFor(year + 1).has(key);
    }

    addBusinessDays(date, days) {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (this.isBusinessDay(result)) added += 1;
      }
      return result;
    }

    update() {
      const cutoff = Number(this.dataset.cutoffHour);
      const processing = Number(this.dataset.processingDays);
      const transitMin = Number(this.dataset.transitMin);
      const transitMax = Math.max(transitMin, Number(this.dataset.transitMax));
      const now = this.nowInZone();
      const beforeCutoff = this.isBusinessDay(now) && now.getHours() < cutoff;

      // Orders placed before the cutoff on a workday are handled today, otherwise on the next business day.
      // Processing days are added on top: 0 = ships on the handling day, 1 = the next business day, etc.
      const handlingDay = beforeCutoff ? now : this.addBusinessDays(now, 1);
      const shipDate = this.addBusinessDays(handlingDay, processing);
      const arriveMin = this.addBusinessDays(shipDate, transitMin);
      const arriveMax = this.addBusinessDays(shipDate, transitMax);

      const format = (date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      // The arrival range drops the weekday so "Free delivery Oct 15 – Oct 17" fits one line on phones.
      const formatShort = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const countdown = this.querySelector('[data-countdown]');
      if (beforeCutoff) {
        const deadline = new Date(now);
        deadline.setHours(cutoff, 0, 0, 0);
        const minutes = Math.max(1, Math.round((deadline - now) / 60000));
        const hours = Math.floor(minutes / 60);
        this.setText('[data-countdown-time]', hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`);
        this.setText('[data-ship-date]', shipDate.toDateString() === now.toDateString() ? this.dataset.todayLabel : format(shipDate));
        if (countdown?.hidden) countdown.hidden = false;
      } else if (countdown && !countdown.hidden) {
        countdown.hidden = true;
      }

      const prefix = this.dataset.arrivalPrefix ? `${this.dataset.arrivalPrefix} ` : '';
      this.setText('[data-arrival]', `${prefix}${formatShort(arriveMin)} – ${formatShort(arriveMax)}`);

      this.updateHoliday(now, cutoff, processing, transitMax, format);
    }

    subtractBusinessDays(date, days) {
      const result = new Date(date);
      let removed = 0;
      while (removed < days) {
        result.setDate(result.getDate() - 1);
        if (this.isBusinessDay(result)) removed += 1;
      }
      return result;
    }

    /**
     * Last business day (this year or next) on which an order before the cutoff still arrives in time.
     * With `thisYearOnly`, the calculated day of the current year is returned even when it's past.
     */
    lastOrderDay(now, processing, transitMax, thisYearOnly = false) {
      const override = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(this.dataset.holidayLastOrder || '');
      if (override) {
        const date = new Date(Number(override[1]), Number(override[2]) - 1, Number(override[3]));
        return Number.isNaN(date.getTime()) ? null : date;
      }

      const arriveBy = /^(\d{1,2})-(\d{1,2})$/.exec(this.dataset.holidayArriveBy || '');
      if (!arriveBy) return null;
      const month = Number(arriveBy[1]) - 1;
      const day = Number(arriveBy[2]);
      const buffer = Number(this.dataset.holidayBuffer) || 0;

      const forYear = (year) => {
        // Arrivals only land on business days, so start from the last business day on or before the date.
        const latestArrival = new Date(year, month, day);
        while (!this.isBusinessDay(latestArrival)) {
          latestArrival.setDate(latestArrival.getDate() - 1);
        }
        const shipDate = this.subtractBusinessDays(latestArrival, buffer + transitMax);
        return this.subtractBusinessDays(shipDate, processing);
      };

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisYear = forYear(now.getFullYear());
      if (thisYearOnly) return thisYear;
      return thisYear >= today ? thisYear : forYear(now.getFullYear() + 1);
    }

    updateHoliday(now, cutoff, processing, transitMax, format) {
      const holiday = this.querySelector('[data-holiday]');
      if (!holiday || !this.dataset.holidayName) return;

      const orderDay = this.lastOrderDay(now, processing, transitMax);
      const deadline = orderDay && new Date(orderDay);
      deadline?.setHours(cutoff, 0, 0, 0);

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const daysLeft = orderDay ? Math.round((orderDay - today) / 86400000) : -1;
      const showDays = Number(this.dataset.holidayShowDays) || 45;
      let text = '';
      if (orderDay && now < deadline && daysLeft <= showDays) {
        let template = this.dataset.holidayText;
        if (daysLeft === 0) template = this.dataset.holidayTodayText || template;
        else if (daysLeft <= 7) template = this.dataset.holidaySoonText || template;

        text = (template || '')
          .replaceAll('[holiday]', this.dataset.holidayName)
          .replaceAll('[date]', format(orderDay))
          .replaceAll('[days]', daysLeft === 1 ? '1 day' : `${daysLeft} days`)
          .trim();
        if (text) this.setText('[data-holiday-label]', text);
      }

      // Optional: after the deadline (same year) until the "show until" date, suggest the gift card
      const afterLink = this.querySelector('[data-holiday-after]');
      let after = false;
      if (!text && afterLink instanceof HTMLElement && this.dataset.holidayAfterText) {
        const until = /^(\d{1,2})-(\d{1,2})$/.exec(this.dataset.holidayAfterUntil || '');
        const pastDay = this.lastOrderDay(now, processing, transitMax, true);
        if (until && pastDay) {
          const pastDeadline = new Date(pastDay);
          pastDeadline.setHours(cutoff, 0, 0, 0);
          const afterEnd = new Date(pastDay.getFullYear(), Number(until[1]) - 1, Number(until[2]), 23, 59, 59);
          after = now >= pastDeadline && now <= afterEnd;
        }
        if (after) {
          text = this.dataset.holidayAfterText.trim();
          if (afterLink.textContent !== text) afterLink.textContent = text;
        }
      }
      if (afterLink instanceof HTMLElement && afterLink.hidden === after) afterLink.hidden = !after;
      const label = this.querySelector('[data-holiday-label]');
      if (label instanceof HTMLElement && label.hidden !== after) label.hidden = after;

      const visible = text !== '';
      if (holiday.hidden === visible) holiday.hidden = !visible;

      // Holiday-only variant (e.g. the campaign banner): the whole element goes with the deadline.
      if (this.hasAttribute('data-holiday-only') && this.hidden === visible) this.hidden = !visible;

      // Announcement bar variant: the deadline replaces the regular text while it's shown.
      const fallback = this.querySelector('[data-holiday-fallback]');
      if (fallback instanceof HTMLElement && fallback.hidden !== visible) fallback.hidden = visible;
    }
  }

  customElements.define('wt-delivery-estimate', WtDeliveryEstimate);
}
